import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { Panel, WaButton, itDate } from "@/components/kit";
import { EditableRows } from "@/components/EditableRows";
import { api, errMsg, fmtMoney } from "@/lib/api";

export function useTeam(cid) {
  const [t, setT] = useState(null);
  const [opts, setOpts] = useState({ roles: [], supplier_categories: [] });
  const [reg, setReg] = useState({ collaborators: [], suppliers: [], equipment: [] });
  const load = useCallback(() => api.get(`/contracts/${cid}/team`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))), [cid]);
  useEffect(() => {
    load();
    api.get("/team/options").then((r) => setOpts(r.data));
    Promise.all(["collaborators", "suppliers", "equipment"].map((n) => api.get(`/registry/${n}`))).then(([a, b, c]) => setReg({ collaborators: a.data, suppliers: b.data, equipment: c.data }));
  }, [load]);
  const save = (section) => async (rows) => {
    try {
      setT((await api.put(`/contracts/${cid}/team/${section}`, { rows })).data);
      toast.success("Salvato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tpl = async () => {
    try {
      await api.post(`/contracts/${cid}/timeline/template`);
      await load();
      toast.success("Scaletta tipo creata: modificala come serve");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return { t, opts, reg, save, tpl };
}

const PLUR = { "Maître": "maître", Cameriere: "camerieri", Cuoco: "cuochi", Lavapiatti: "lavapiatti", Barman: "barman" };

const fill = (list, idKey, map) => (k, v, row) => {
  if (k !== idKey) return row;
  const x = list.find((i) => i.id === v);
  return x ? { ...row, ...map(x) } : row;
};

export function TeamPanel({ c }) {
  const { t, opts, reg, save, tpl } = useTeam(c.id);
  if (!t) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const ev = t.evento;
  const convoke = (s) => `👋 Ciao *${s.nome}*,\n\n📅 ti confermiamo per *${ev.evento_tipo || "l'evento"}* del *${itDate(ev.evento_data)}* presso *${ev.sede}*.\n⏰ Turno: *${s.dalle || "?"} – ${s.alle || "?"}* come *${s.ruolo || "staff"}*.${s.note ? `\n📝 ${s.note}` : ""}\n\n✅ Rispondi OK per confermare. Grazie! 🌊`;
  const sugg = t.suggestion.filter((s) => s.ruolo);
  const have = (r) => t.staff.filter((s) => s.ruolo === r).length;
  return (
    <div className="space-y-6">
      <Panel title="Personale" testId="staff-panel" right={<span className="text-sm">Costo previsto <b data-testid="staff-cost">{fmtMoney(t.costo_personale)}</b></span>}>
        {sugg.length > 0 && (
          <div className="rounded-lg bg-[#EAF5F7] p-3 text-sm flex flex-wrap gap-x-4 gap-y-1 items-center" data-testid="staff-suggestion">
            <span className="inline-flex items-center gap-1 font-medium"><Users size={14} /> Consigliati per {t.suggestion.at(-1).ospiti} ospiti:</span>
            {sugg.map((s) => <span key={s.ruolo} className={have(s.ruolo) >= s.n ? "text-[#166534]" : "text-[#B45309]"}>{s.n} {s.n > 1 ? PLUR[s.ruolo] || s.ruolo : s.ruolo.toLowerCase()} ({have(s.ruolo)}/{s.n})</span>)}
          </div>
        )}
        <EditableRows testId="staff" rows={t.staff} onSave={save("staff")} addLabel="Aggiungi persona"
          onPick={fill(reg.collaborators, "collab_id", (x) => ({ nome: x.nome, ruolo: x.ruolo, tel: x.tel, costo_orario: x.costo_orario }))}
          cols={[{ k: "collab_id", label: "Dall'elenco", type: "select", options: reg.collaborators.map((x) => ({ value: x.id, label: x.nome })), w: "17%" },
            { k: "nome", label: "Nome" }, { k: "ruolo", label: "Ruolo", type: "select", options: opts.roles }, { k: "tel", label: "Telefono" },
            { k: "dalle", label: "Dalle", type: "time", w: "100px" }, { k: "alle", label: "Alle", type: "time", w: "100px" }, { k: "costo_orario", label: "€/ora", type: "number", w: "80px" },
            { k: "wa", label: "Turno", render: (s) => (s.nome ? <WaButton phone={s.tel} text={convoke(s)} label="Invia" testId={`staff-wa-${s.nome}`} /> : null) }]} />
      </Panel>
      <Panel title="Scaletta dell'evento" testId="timeline-panel" right={!t.timeline.length && <button className="text-xs rounded-full border border-[#34B3B0] text-[#0B6E8F] px-3 py-1.5" onClick={tpl} data-testid="timeline-template">Usa scaletta tipo</button>}>
        <p className="text-sm text-[#47606B] -mt-2">Minuto per minuto per cucina e sala. Spunta «Cliente» sui momenti da mostrare anche nell'area riservata del cliente.</p>
        <EditableRows testId="timeline" rows={t.timeline} onSave={save("timeline")} addLabel="Aggiungi momento"
          cols={[{ k: "ora", label: "Ora", type: "time", w: "110px" }, { k: "attivita", label: "Momento", ph: "Ingresso sposi, taglio torta…", w: "34%" }, { k: "chi", label: "Chi se ne occupa" }, { k: "note", label: "Note" }, { k: "cliente", label: "Cliente", type: "check", w: "60px" }]} />
      </Panel>
    </div>
  );
}

export function SuppliersPanel({ c }) {
  const { t, opts, reg, save } = useTeam(c.id);
  if (!t) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="space-y-6">
      <Panel title="Fornitori e partner dell'evento" testId="ev-suppliers" right={<span className="text-sm">Provvigioni <b data-testid="supplier-commission">{fmtMoney(t.commissioni)}</b></span>}>
        <EditableRows testId="evsup" rows={t.suppliers} onSave={save("suppliers")} addLabel="Aggiungi fornitore"
          onPick={fill(reg.suppliers, "supplier_id", (x) => ({ nome: x.nome, categoria: x.categoria, tel: x.tel, commissione: x.commissione }))}
          cols={[{ k: "supplier_id", label: "Dall'elenco", type: "select", options: reg.suppliers.map((x) => ({ value: x.id, label: x.nome })), w: "17%" }, { k: "nome", label: "Nome" },
            { k: "categoria", label: "Categoria", type: "select", options: opts.supplier_categories }, { k: "tel", label: "Telefono" }, { k: "arrivo", label: "Arrivo", type: "time", w: "100px" },
            { k: "importo", label: "Importo €", type: "number", w: "100px" }, { k: "commissione", label: "Provv. %", type: "number", w: "80px" }, { k: "note", label: "Note" }]} />
      </Panel>
      <Panel title="Attrezzature e allestimenti" testId="ev-equipment">
        {!reg.equipment.length && <p className="text-sm text-[#7B929C]">Aggiungete prima gli articoli in Squadra › Attrezzature.</p>}
        <EditableRows testId="eveq" rows={t.equipment} onSave={save("equipment")} addLabel="Aggiungi articolo"
          onPick={fill(reg.equipment, "item_id", (x) => ({ nome: x.nome }))}
          cols={[{ k: "item_id", label: "Articolo", type: "select", options: reg.equipment.map((x) => ({ value: x.id, label: x.nome })), w: "40%" }, { k: "quantita", label: "Quantità", type: "number", w: "120px" },
            { k: "disp", label: "Disponibilità nel giorno", render: (e) => { if (e.totale === undefined) return <span className="text-xs text-[#7B929C]">salva per verificare</span>; const free = e.totale - e.altrove; const ok = Number(e.quantita || 0) <= free; return <span className={`text-xs ${ok ? "text-[#166534]" : "text-[#991B1B] font-semibold"}`} data-testid={`eq-avail-${e.nome}`}>{ok ? `OK • liberi ${free}/${e.totale}` : `Mancano ${Number(e.quantita) - free}! (${e.altrove} già a ${e.contratti.join(", ")})`}</span>; } }]} />
      </Panel>
    </div>
  );
}
